import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../components/Alert';
import { useConfirm } from '../components/Confirm';
import { useQuestionnairePrompt } from '../components/QuestionnairePrompt';
import { leagueAPI, userAPI, levelsAPI, messageAPI } from '../services/api';
import MapPicker from '../components/MapPicker';
import ChatBox from '../components/ChatBox';
import './SuperLeaguePage.css';

const SuperLeaguePage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin: isGlobalAdmin } = useAuth();
  const { showAlert } = useAlert();
  const { showConfirm } = useConfirm();
  const { showQuestionnairePrompt } = useQuestionnairePrompt();
  const [superLeague, setSuperLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCombinedRanking, setShowCombinedRanking] = useState(false);
  const [showLocationMap, setShowLocationMap] = useState(true);
  const [showSubleagueStandings, setShowSubleagueStandings] = useState(true);
  const [showContinuationHistory, setShowContinuationHistory] = useState(false);
  const [canCreateContinuation, setCanCreateContinuation] = useState(true);
  const [continuationLimitMessage, setContinuationLimitMessage] = useState('');
  const [chatBoxOpen, setChatBoxOpen] = useState(false);
  const [selectedChatUser, setSelectedChatUser] = useState(null);

  const formatLevel = (value) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return 0;
    }
    return Math.round(numeric);
  };

  const formatDateDisplay = (value) => {
    if (!value) return 'TBD';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'TBD';
    return parsed.toLocaleDateString();
  };

  const getMatchFormatDisplay = (format) => {
    const formats = {
      'best-of-3-sets-to-6': 'Best of 3 Sets (to 6)',
      'best-of-3-sets-to-4': 'Best of 3 Sets (to 4)',
      'best-of-3-deciding-tiebreak-sets-to-6': 'Best of 3 (3rd set tiebreak)',
      'best-of-3-deciding-tiebreak-sets-to-4': 'Best of 3 (3rd set tiebreak, sets to 4)',
      'single-set': 'Single Set',
      'squash-best-of-5-games-to-11': 'Best of 5 Games (First to 11, win by 2)',
      'squash-best-of-3-games-to-11': 'Best of 3 Games (First to 11, win by 2)',
    };
    return formats[format] || format;
  };

  useEffect(() => {
    loadSuperLeague();
  }, [leagueId, isGlobalAdmin]);

  // Check active leagues limit for continuation
  useEffect(() => {
    const checkContinuationLimit = async () => {
      if (!superLeague || !user || isGlobalAdmin) {
        // Global admins are not limited
        setCanCreateContinuation(true);
        return;
      }

      const isLeagueAdmin = superLeague.createdBy === user.userId;
      if (!isLeagueAdmin) {
        setCanCreateContinuation(true);
        return;
      }

      try {
        const userResponse = await userAPI.getUser(user.userId);
        const userData = userResponse.data;
        const maxActiveLeagues = userData?.maxActiveLeagues ?? 1;

        const createdLeaguesResponse = await userAPI.getCreatedLeagues(user.userId);
        const createdLeagues = createdLeaguesResponse.data || [];

        const now = new Date();
        // Set time to end of day for proper date comparison
        now.setHours(23, 59, 59, 999);
        
        const activeLeagues = createdLeagues.filter(l => {
          // Exclude subleagues - only count standalone leagues and superleagues
          if (l.superLeagueId) return false;
          // Exclude the current league from the count
          if (l.leagueId === superLeague.leagueId) return false;
          // League is active if it has no endDate or endDate is in the future (or today)
          if (!l.endDate) return true;
          const endDate = new Date(l.endDate);
          if (Number.isNaN(endDate.getTime())) return true; // Treat invalid dates as active
          // Set endDate to end of day for comparison
          endDate.setHours(23, 59, 59, 999);
          return endDate >= now;
        });

        console.log('Continuation active leagues check (super league):', {
          userId: user.userId,
          currentLeagueId: superLeague.leagueId,
          maxActiveLeagues,
          activeLeaguesCount: activeLeagues.length,
          activeLeagues: activeLeagues.map(l => ({ id: l.leagueId, name: l.name, endDate: l.endDate }))
        });

        if (activeLeagues.length >= maxActiveLeagues) {
          setCanCreateContinuation(false);
          setContinuationLimitMessage(`You have reached your limit of ${maxActiveLeagues} active league(s). Please wait for a league to end before creating a follow on league.`);
        } else {
          setCanCreateContinuation(true);
          setContinuationLimitMessage('');
        }
      } catch (err) {
        console.error('Failed to check continuation limit:', err);
        // On error, allow creation (backend will enforce the limit)
        setCanCreateContinuation(true);
        setContinuationLimitMessage('');
      }
    };

    checkContinuationLimit();
  }, [superLeague, user, isGlobalAdmin]);

  const loadSuperLeague = async () => {
    try {
      const response = await leagueAPI.getSuperLeague(leagueId);
      const data = response.data;
      setSuperLeague(data);
      const hasAccess = data?.isMember || data?.isAdmin || isGlobalAdmin;
      setShowLocationMap(!hasAccess);
    } catch (err) {
      console.error('Failed to load super league:', err);
      setError('Failed to load super league');
    } finally {
      setLoading(false);
    }
  };

  const getCombinedRanking = () => {
    if (!superLeague || !Array.isArray(superLeague.subLeagues)) return [];
    const rows = [];
    const seen = new Set();
    superLeague.subLeagues.forEach(sl => {
      const slName = sl.name;
      const slId = sl.leagueId;
      const players = Array.isArray(sl.players) ? sl.players : [];
      players.forEach(p => {
        const userId = typeof p === 'string' ? p : p.userId;
        if (!userId || seen.has(userId)) return;
        seen.add(userId);
        const name = typeof p === 'string' ? '' : (p.name || '');
        const email = typeof p === 'string' ? '' : (p.email || '');
        const level = typeof p === 'string' ? 0 : (p.level ?? 0);
        rows.push({ userId, name, email, level, subLeagueName: slName, subLeagueId: slId });
      });
    });
    // Sort by level desc, then name asc
    rows.sort((a, b) => {
      if ((a.level || 0) !== (b.level || 0)) return (b.level || 0) - (a.level || 0);
      return (a.name || '').localeCompare(b.name || '');
    });
    return rows;
  };

  const handleExpressInterest = async () => {
    try {
      // Check if user has a level for this super league's sport
      if (superLeague?.sport && user?.userId) {
        const levelCheck = await levelsAPI.checkUserLevel(user.userId, superLeague.sport);
        if (!levelCheck.data.hasLevel) {
          // User doesn't have a level for this sport, show questionnaire prompt
          showQuestionnairePrompt(superLeague.sport);
          return;
        }
      }

      await leagueAPI.expressInterest(leagueId);
      showAlert('Interest expressed successfully! The league admin will review your request.', 'success');
      loadSuperLeague();
    } catch (err) {
      console.error('Express interest error:', err);
      showAlert(err.response?.data?.error || 'Failed to express interest', 'error');
    }
  };

  const handleRemoveInterest = async () => {
    showConfirm('Are you sure you want to withdraw your interest from this super league?', async () => {
      try {
        await leagueAPI.removeInterest(leagueId);
        showAlert('Interest removed successfully.', 'success');
        loadSuperLeague();
      } catch (err) {
        console.error('Remove interest error:', err);
        showAlert(err.response?.data?.error || 'Failed to remove interest', 'error');
      }
    });
  };

  const handleCreateContinuation = async () => {
    if (!superLeague) return;
    
    // Check if a follow on league already exists
    if (superLeague.nextLeagueId) {
      showAlert('A follow on league already exists for this super league.', 'warning');
      return;
    }
    
    // Check if user has reached their active league limit
    if (!canCreateContinuation && continuationLimitMessage) {
      showAlert(continuationLimitMessage, 'warning');
      return;
    }
    
    // Navigate to create league page with previous league data
    navigate('/leagues/create', {
      state: {
        continuationFrom: {
          leagueId: superLeague.leagueId,
          name: superLeague.name,
          isSuperLeague: true,
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading super league...</div>
      </div>
    );
  }

  if (error || !superLeague) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="error-message">{error || 'Super league not found'}</div>
          <Link to="/leagues" className="btn btn-primary">Back to Leagues</Link>
        </div>
      </div>
    );
  }

  const hasSubleagues = superLeague.subLeagues && superLeague.subLeagues.length > 0;
  const isApprovedMember = Boolean(superLeague?.players && superLeague.players.some((p) => {
    if (!p) return false;
    if (typeof p === 'string') return p === user?.userId;
    if (typeof p === 'object') return p.userId === user?.userId;
    return false;
  }));

  const canShowInterestButton =
    Boolean(user) && !isApprovedMember;
  const hasLeagueEnded = (() => {
    if (!superLeague?.endDate) return false;
    const end = new Date(superLeague.endDate);
    return !Number.isNaN(end.getTime()) && new Date() >= end;
  })();
  const hasContinuation = Boolean(superLeague?.nextLeagueId);
  const allowContinuationButton = Boolean(superLeague?.isAdmin || isGlobalAdmin);
  const continuationButtonDisabled = !hasLeagueEnded;
  const continuationButtonTitle = !hasLeagueEnded
    ? 'Follow on leagues can be created once this super league has finished.'
    : '';

  return (
    <div className="page-container">
      <div className="container">
        {/* Header */}
        <div className="league-header" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {superLeague.coverPhotoUrl && (
            <div style={{ width: 144, height: 144, borderRadius: 8, overflow: 'hidden', background: '#f3f4f6', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={superLeague.coverPhotoUrl}
                alt="Super league cover"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
              <h1 className="page-title" style={{ margin: 0 }}>{superLeague.name}</h1>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                {canShowInterestButton && (
                  superLeague.isInterested ? (
                    <button onClick={handleRemoveInterest} className="btn btn-secondary">
                      Withdraw Interest
                    </button>
                  ) : (
                    <button onClick={handleExpressInterest} className="btn btn-primary">
                      Express Interest to Join
                    </button>
                  )
                )}
              {(superLeague.isAdmin || isGlobalAdmin) && (
                  <>
                    {allowContinuationButton && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleCreateContinuation}
                          disabled={continuationButtonDisabled}
                          title={continuationButtonTitle}
                        >
                          Create Follow on League
                        </button>
                    )}
                  <Link to={`/super-leagues/${leagueId}/members`} className="btn btn-primary">
                    Edit Members
                  </Link>
                  <Link to={`/super-leagues/${leagueId}/edit`} className="btn btn-secondary">
                    Edit Super League
                  </Link>
                  </>
                )}
                </div>
            </div>
            <div className="league-meta">
              <span className="badge badge-info">{superLeague.sport}</span>
              <span className="badge badge-warning">Super League</span>
              <span>👥 Players: {superLeague.players?.length || 0} · ⭐ Interested: {superLeague.interestedUsers?.filter(u => u.status !== 'approved').length || 0}</span>
              <span>🎾 {getMatchFormatDisplay(superLeague.matchFormat)}</span>
              {superLeague.startDate && (
                <span>📅 Start: {new Date(superLeague.startDate).toLocaleDateString()}</span>
              )}
              {superLeague.endDate && (
                <span>🏁 End: {new Date(superLeague.endDate).toLocaleDateString()}</span>
              )}
              {superLeague.createdByName && (
                <span>👤 Created by: <Link to={`/players/${superLeague.createdBy}`}>{superLeague.createdByName}</Link></span>
              )}
            </div>
          </div>
        </div>

        {/* Location Map */}
        {superLeague.latitude && superLeague.longitude && (
          <div className="card">
            <div className="collapsible-card-header">
              <h2>League Location</h2>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowLocationMap((prev) => !prev)}
              >
                {showLocationMap ? 'Hide Map' : 'Show Map'}
              </button>
            </div>
            {showLocationMap && (
              <MapPicker
                initialLocation={{ lat: parseFloat(superLeague.latitude), lng: parseFloat(superLeague.longitude) }}
                readOnly={true}
                height="300px"
              />
            )}
          </div>
        )}

        <div className="card">
          <div className="collapsible-card-header">
            <h2>All-Player Ranking (All Subleagues)</h2>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowCombinedRanking((prev) => !prev)}
            >
              {showCombinedRanking ? 'Hide Rankings' : 'Show Rankings'}
            </button>
          </div>
          {showCombinedRanking && (
            (() => {
              const rows = getCombinedRanking();
              if (rows.length === 0) return <p>No results yet.</p>;
              return (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Player</th>
                        <th>Level</th>
                        <th>Subleague</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={`${r.userId}-${r.subLeagueId}`}>
                          <td>{idx + 1}</td>
                          <td><Link to={`/players/${r.userId}`}>{r.name}</Link></td>
                          <td><strong>{formatLevel(r.level)}</strong></td>
                          <td>
                            <Link to={`/leagues/${r.subLeagueId}`}>
                              {(() => {
                                if (!r.subLeagueName) return '';
                                const idx = r.subLeagueName.toLowerCase().indexOf('division');
                                if (idx === -1) return r.subLeagueName;
                                return r.subLeagueName.slice(idx).trim();
                              })()}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()
          )}
        </div>

        {/* Combined ranking (available to all viewers) */}
        {/* Subleague standings */}
        {hasSubleagues && (
          <div className="card">
            <div className="collapsible-card-header">
              <h2>Subleague Standings</h2>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowSubleagueStandings((prev) => !prev)}
              >
                {showSubleagueStandings ? 'Hide Standings' : 'Show Standings'}
              </button>
            </div>
            {showSubleagueStandings && (
              superLeague.subLeagues.map((subLeagueSummary) => (
                <div key={subLeagueSummary.leagueId} className="subleague-section">
                  <div className="subleague-header" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {subLeagueSummary.coverPhotoUrl && (
                      <div style={{ width: 96, height: 96, borderRadius: 6, overflow: 'hidden', background: '#f3f4f6', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img
                          src={subLeagueSummary.coverPhotoUrl}
                          alt="Subleague cover"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </div>
                    )}
                    <h3 style={{ margin: 0 }}>{subLeagueSummary.name}</h3>
                    <Link to={`/leagues/${subLeagueSummary.leagueId}`} className="btn btn-sm btn-primary">
                      View Full League
                    </Link>
                  </div>
                  {subLeagueSummary.standings && subLeagueSummary.standings.length > 0 ? (
                    <div className="table-container">
                      <table className="table subleague-standings-table">
                        <thead>
                          <tr>
                            <th>Rank</th>
                            <th>Player</th>
                            <th>Points</th>
                            <th className="hide-on-mobile">Wins</th>
                            <th className="hide-on-mobile">Losses</th>
                            <th className="hide-on-mobile">Game Diff</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subLeagueSummary.standings.map((player, playerIndex) => (
                            <tr key={player.userId}>
                              <td>{playerIndex + 1}</td>
                              <td>
                                <Link to={`/players/${player.userId}`}>{player.name}</Link>
                              </td>
                              <td><strong>{player.points}</strong></td>
                              <td className="hide-on-mobile">{player.wins}</td>
                              <td className="hide-on-mobile">{player.losses}</td>
                              <td className="hide-on-mobile">{player.gameDiff > 0 ? '+' : ''}{player.gameDiff}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p>No matches played yet in this subleague.</p>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {(superLeague.isAdmin || isGlobalAdmin) && !hasSubleagues && (
          <div className="card">
            <h2>No Subleagues Created Yet</h2>
            <p>Once you accept players, they will be automatically split into subleagues based on their skill level.</p>
            <p>Current players: {superLeague.players?.length || 0}</p>
            <p>Interested users: {superLeague.interestedUsers?.filter(u => u.status !== 'approved').length || 0}</p>
          </div>
        )}
        {(superLeague?.previousLeagueId || superLeague?.nextLeagueId || (Array.isArray(superLeague?.continuationSeries) && superLeague.continuationSeries.length > 0)) && (
          <div className="card">
            <div className="collapsible-card-header">
              <h2>History & Follow on Leagues</h2>
              {Array.isArray(superLeague.continuationSeries) && superLeague.continuationSeries.length > 0 && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowContinuationHistory((prev) => !prev)}
                >
                  {showContinuationHistory ? 'Hide Full History' : 'Show Full History'}
                </button>
              )}
            </div>
            
            {/* Quick view: Previous and Next leagues - only show when full history is hidden */}
            {!showContinuationHistory && (superLeague?.previousLeagueId || superLeague?.nextLeagueId) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {superLeague.previousLeagueId && (() => {
                  const prevLeague = Array.isArray(superLeague.continuationSeries) 
                    ? superLeague.continuationSeries.find(item => item.leagueId === superLeague.previousLeagueId)
                    : null;
                  if (!prevLeague) return null;
                  const targetPath = prevLeague.isSuperLeague
                    ? `/super-leagues/${prevLeague.leagueId}`
                    : `/leagues/${prevLeague.leagueId}`;
                  return (
                    <div
                      style={{
                        border: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb',
                        borderRadius: '10px',
                        padding: '12px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ flex: '1 1 auto', minWidth: 200 }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 600 }}>
                          Previous League
                        </div>
                        <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: 4 }}>
                          {prevLeague.name}
                        </div>
                        <div style={{ fontSize: 14, color: '#4b5563' }}>
                          <div>
                            {formatDateDisplay(prevLeague.startDate)} – {formatDateDisplay(prevLeague.endDate)}
                          </div>
                        </div>
                      </div>
                      <Link to={targetPath} className="btn btn-sm btn-secondary">
                        View League
                      </Link>
                    </div>
                  );
                })()}
                
                {superLeague.nextLeagueId && (() => {
                  const nextLeague = Array.isArray(superLeague.continuationSeries) 
                    ? superLeague.continuationSeries.find(item => item.leagueId === superLeague.nextLeagueId)
                    : null;
                  if (!nextLeague) return null;
                  const targetPath = nextLeague.isSuperLeague
                    ? `/super-leagues/${nextLeague.leagueId}`
                    : `/leagues/${nextLeague.leagueId}`;
                  return (
                    <div
                      style={{
                        border: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb',
                        borderRadius: '10px',
                        padding: '12px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ flex: '1 1 auto', minWidth: 200 }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 600 }}>
                          Next League
                        </div>
                        <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: 4 }}>
                          {nextLeague.name}
                        </div>
                        <div style={{ fontSize: 14, color: '#4b5563' }}>
                          <div>
                            {formatDateDisplay(nextLeague.startDate)} – {formatDateDisplay(nextLeague.endDate)}
                          </div>
                        </div>
                      </div>
                      <Link to={targetPath} className="btn btn-sm btn-secondary">
                        View League
                      </Link>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Full history view */}
            {showContinuationHistory && Array.isArray(superLeague.continuationSeries) && superLeague.continuationSeries.length > 0 && (() => {
              // Sort by start date (latest first)
              const sortedSeries = [...superLeague.continuationSeries].sort((a, b) => {
                const dateA = a.startDate ? new Date(a.startDate).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
                const dateB = b.startDate ? new Date(b.startDate).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
                return dateB - dateA; // Descending order (latest first)
              });
              
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {sortedSeries.map((item) => {
                    const isCurrentLeague = item.leagueId === leagueId;
                    const isPreviousLeague = item.leagueId === superLeague.previousLeagueId;
                    const isNextLeague = item.leagueId === superLeague.nextLeagueId;
                    const statusLabel = item.status
                      ? item.status.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                      : 'Unknown';
                    const targetPath = item.isSuperLeague
                      ? `/super-leagues/${item.leagueId}`
                      : `/leagues/${item.leagueId}`;
                    return (
                      <div
                        key={item.leagueId}
                        style={{
                          border: isCurrentLeague ? '1px solid #2563eb' : '1px solid #e5e7eb',
                          backgroundColor: isCurrentLeague ? '#eff6ff' : '#ffffff',
                          borderRadius: '10px',
                          padding: '12px',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '12px',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div style={{ flex: '1 1 auto', minWidth: 200 }}>
                          {(isPreviousLeague || isNextLeague) && (
                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 600 }}>
                              {isPreviousLeague ? 'Previous League' : 'Next League'}
                            </div>
                          )}
                          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: 4 }}>
                            {item.name}
                            {isCurrentLeague && (
                              <span className="badge badge-info" style={{ marginLeft: 8 }}>
                                Current League
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 14, color: '#4b5563' }}>
                            <div>
                              {formatDateDisplay(item.startDate)} – {formatDateDisplay(item.endDate)}
                            </div>
                            <div>Status: {statusLabel}</div>
                          </div>
                        </div>
                        <Link to={targetPath} className="btn btn-sm btn-secondary">
                          View League
                        </Link>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {chatBoxOpen && selectedChatUser && (
        <ChatBox
          otherUserId={selectedChatUser.userId}
          otherUserName={selectedChatUser.name}
          onClose={() => {
            setChatBoxOpen(false);
            setSelectedChatUser(null);
          }}
        />
      )}
    </div>
  );
};

export default SuperLeaguePage;




